// The elemental system (Godot scripts/autoload/elemental_system.gd, scripts/loadout/element.gd and the twenty
// data/loadout/elemental/*.tres; Nick 2026-10-22). Fire, Ice, Shock, Acid and Magnetic live on a cowboy's guns at
// levels 1-4 (installed through the card drafts: sim/cards.ts), proc by level on every hit, and run a
// rock-paper-scissors ring against elemental robots: FIRE beats ACID beats MAGNETIC beats SHOCK beats ICE beats FIRE.
// Robots standing in water are wet (shock electrocutes, ice flash-freezes, fire goes out); two elements live on one
// robot confuse it (its detonation hits harder); elemental grenades leave hazards that the element that beats them
// turns into a reaction. Everything here is server-side rules on the Enemy and PlayerState; the client only draws.

import { DT, Rng, len2, secs } from './core';
import { AP, ENEMIES, EType } from './defs';
import { Ev } from './codec';
import type { Enemy } from './enemies';
import type { PlayerState } from './player';
import { Elem } from './weapons';
import type { World } from './world';

/** the five elements as a dense index (Elem has gaps): Fire 0, Acid 1, Shock 2, Ice 3, Magnetic 4 */
export const ELEMS: Elem[] = [Elem.Fire, Elem.Acid, Elem.Shock, Elem.Ice, Elem.Magnetic];
export const EL_NAME: Record<number, string> = { [Elem.Fire]: 'FIRE', [Elem.Acid]: 'ACID', [Elem.Shock]: 'SHOCK', [Elem.Ice]: 'ICE', [Elem.Magnetic]: 'MAGNETIC' };
export const EL_L4_NAME: Record<number, string> = { [Elem.Fire]: 'HELLFIRE', [Elem.Acid]: 'CORROSIVE PAYLOAD', [Elem.Shock]: 'TEMPEST', [Elem.Ice]: 'ABSOLUTE ZERO', [Elem.Magnetic]: 'SINGULARITY' };
export const elIndex = (e: number) => ELEMS.indexOf(e as Elem);

/** the ring (element.gd counter_of): what `a` beats */
export function beats(a: number, b: number) {
  return (a === Elem.Fire && b === Elem.Acid) || (a === Elem.Acid && b === Elem.Magnetic) || (a === Elem.Magnetic && b === Elem.Shock) || (a === Elem.Shock && b === Elem.Ice) || (a === Elem.Ice && b === Elem.Fire);
}
export const WEAKNESS_MUL = 1.5;
export const WEAKNESS_STRIP = secs(2);

// ---- by level (index 1..4; element.gd and the .tres files)
export const PROC_CHANCE = [0, 0.2, 0.35, 0.5, 1.0];
/** an elemental gun hits a little softer until L4 (element.gd player_damage_modifier) */
export const EL_DMG_MOD = [1, 0.95, 0.9, 0.85, 1.0];
const FIRE_DPS = [0, 8, 12, 16, 14], FIRE_SECS = [0, 3, 4, 5, 4];
const HELLFIRE_R = 3, HELLFIRE_SPREAD = 0.7, HELLFIRE_HEAT = 3;
const ICE_STACKS = [0, 3, 3, 3, 1], ICE_SLOW = [0, 0.15, 0.18, 0.22, 0.6];
const FREEZE_SECS = 3, FLASH_FREEZE_SECS = 3.5, FROZEN_DMG_MUL = 1.5;
const SHOCK_R = [0, 4, 5, 6, 8], SHOCK_RATIO = [0, 0.5, 0.6, 0.7, 1.0], SHOCK_N = [0, 1, 2, 3, 5], SHOCK_STUN = [0, 0.3, 0.45, 0.6, 0.85];
const ACID_TIERS = [0, 1, 1, 2, 99], ACID_SECS = [0, 5, 10, 10, 999];
const MAG_CUT = [0, 0.3, 0.4, 0.5, 0.75], MAG_SECS = 10, SINGULARITY_R = 5, SINGULARITY_FORCE = 14;
/** wet: in water for this long after leaving; wet + shock electrocutes everything wet within WET_SHOCK_R */
const WET_SECS = secs(4), WET_SHOCK_DMG = 45, WET_SHOCK_R = 9, WET_BACKFIRE = 14;
/** two different elements within CONFUSION_SECS of each other confuse the robot: its detonation hits CONFUSION_MUL x */
export const CONFUSION_SECS = secs(5), CONFUSION_MUL = 1.5;

/** what the rules need from the game (sim/game.ts implements it) */
export interface ElemHost {
  enemies: Enemy[];
  players: Map<number, PlayerState>;
  world: World;
  tick: number;
  rng: Rng;
  emit(t: Ev, f: number[], s?: string): void;
  damageEnemy(e: Enemy, amount: number, ap: number, by: number, flags: number, hx: number, hy: number, hz: number, bypassArmor?: boolean, bypassShield?: boolean): boolean;
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
  friendlyHurt(p: PlayerState, by: number, dmg: number, fx: number, fz: number, kx?: number, kz?: number, bypassShield?: boolean): boolean;
  stagger(e: Enemy, force: number): void;
  procd?(e: Enemy, elem: number, owner: number): void;
  /** the owner's hand (sim/cards.ts): Dowsing Rounds, Catalyst and the conduit set reach into the rules */
  handOf?(owner: number): { wetChain: number; reactionMul: number } | undefined;
}

// ------------------------------------------------------------------ installs on a cowboy's guns
/** the element on weapon slot 0/1 (Elem, or None) and its level */
export function gunElem(p: PlayerState, slot: number): number { const v = slot === 0 ? p.el0 : slot === 1 ? p.el1 : 0; return v ? v - 1 : Elem.None; }
export function gunLevel(p: PlayerState, slot: number): number { return slot === 0 ? p.elL0 : slot === 1 ? p.elL1 : 0; }
/** Godot loadout_manager.gd equip_element_by_type: empty slot gets L1; the same element levels up (cap 3); a different element
 *  takes the slot at the level it had (clamped 1..3). Returns the level now on the slot. */
export function installElem(p: PlayerState, slot: number, elem: number): number {
  const cur = gunElem(p, slot), lvl = gunLevel(p, slot);
  let next = 1;
  if (cur === Elem.None) next = 1;
  else if (cur === elem) next = Math.min(3, lvl + 1);
  else next = Math.max(1, Math.min(3, lvl));
  if (slot === 0) { p.el0 = elem + 1; p.elL0 = next; } else { p.el1 = elem + 1; p.elL1 = next; }
  return next;
}
/** the L4 legendary: only onto a gun that holds that element at L3 (either slot); returns the slot, or -1 */
export function installL4(p: PlayerState, elem: number): number {
  for (const s of [0, 1]) if (gunElem(p, s) === elem && gunLevel(p, s) >= 3) { if (s === 0) p.elL0 = 4; else p.elL1 = 4; return s; }
  return -1;
}
export const hasL3 = (p: PlayerState, elem: number) => [0, 1].some((s) => gunElem(p, s) === elem && gunLevel(p, s) === 3);
export function clearElems(p: PlayerState) { p.el0 = p.el1 = 0; p.elL0 = p.elL1 = 0; }

// ------------------------------------------------------------------ robot-side state (fields on Enemy, see enemies.ts)
export function tickEnemyElems(e: Enemy, h: ElemHost) {
  // wet while standing in water, and for a while after
  if (!e.airborne && h.world.wetAt(e.x, e.z) && h.world.waterAt(e.x, e.z) > e.y + 0.3) e.wetT = WET_SECS;
  else if (e.wetT > 0) e.wetT--;
  if (e.wetT > 0 && e.burnT > 0) { e.burnT = 0; } // water puts fire out
  if (e.frozenT > 0 && --e.frozenT === 0) { e.iceN = 0; e.iceSlow = 0; } // thaw: the stacks go with it
  if (e.magT > 0 && --e.magT === 0) e.magCut = 0;
  for (let i = 0; i < 5; i++) if (e.elUntil[i] > 0 && e.elUntil[i] <= h.tick) e.elUntil[i] = 0;
}
/** two or more elements live on it (Godot ElementalSystem.is_confused) */
export function isConfused(e: Enemy, tick: number) { let n = 0; for (let i = 0; i < 5; i++) if (e.elUntil[i] > tick) n++; return n >= 2; }
/** a robot carries an element (armor_element): rolled at spawn by village (encounter_manager FLOOR_ELEMENT_CHANCE), fixed on some types */
export function rollArmorEl(type: EType, village: number, rng: Rng): number {
  if (type === EType.MetalBrute) return Elem.None; // metal is forced NONE
  const chance = [0, 0, 0.1, 0.25, 0.5][Math.min(4, village)];
  if (chance <= 0 || rng.next() >= chance) return Elem.None; // no roll where there's no chance: the early seeds stay as they were
  return ELEMS[rng.int(0, 4)];
}

// ------------------------------------------------------------------ the proc
/** An elemental hit landed on a robot: roll by level, then apply (Godot try_proc_on_hit). `amount` is the hit's damage
 *  (shock chains carry a share of it). Returns true if it procced. `force` skips the roll (dash trails, hazards). */
export function tryProc(h: ElemHost, e: Enemy, elem: number, level: number, owner: number, amount: number, force = false): boolean {
  if (e.dead || elem === Elem.None || level <= 0) return false;
  if (!force && h.rng.next() >= PROC_CHANCE[Math.min(4, level)]) return false;
  applyProc(h, e, elem, level, owner, amount);
  return true;
}

export function applyProc(h: ElemHost, e: Enemy, elem: number, level: number, owner: number, amount: number) {
  if (e.dead || elem === Elem.None) return;
  const L = Math.max(1, Math.min(4, level));
  const idx = elIndex(elem);
  if (idx >= 0) e.elUntil[idx] = h.tick + CONFUSION_SECS;
  const op = owner >= 0 ? h.players.get(owner) : undefined;
  switch (elem) {
    case Elem.Fire: {
      if (e.wetT > 0) { h.emit(Ev.Proc, [e.id, elem]); return; } // it hisses off a wet robot
      burn(e, FIRE_DPS[L], secs(FIRE_SECS[L]), owner);
      if (L === 4) {
        // Hellfire: the fire jumps to robots near it, and feeds the wielder's pack
        for (const o of h.enemies) if (o !== e && !o.dead && len2(o.x - e.x, o.z - e.z) < HELLFIRE_R + ENEMIES[o.type].radius) burn(o, FIRE_DPS[L] * HELLFIRE_SPREAD, secs(FIRE_SECS[L]), owner);
        if (op) op.heat = Math.min(999, op.heat + HELLFIRE_HEAT);
      }
      break;
    }
    case Elem.Ice: {
      if (e.wetT > 0) {
        // flash-freeze: the wet is spent, the robot locks solid, and everything wet near it in the same water chills
        e.wetT = 0; freeze(e, secs(FLASH_FREEZE_SECS));
        for (const o of h.enemies) if (o !== e && !o.dead && o.wetT > 0 && len2(o.x - e.x, o.z - e.z) < 22) freeze(o, secs(2.2));
        break;
      }
      if (e.frozenT > 0) break;
      e.iceN = Math.min(ICE_STACKS[L], e.iceN + 1);
      e.iceSlow = e.iceN * ICE_SLOW[L];
      if (e.iceN >= ICE_STACKS[L]) freeze(e, secs(FREEZE_SECS));
      break;
    }
    case Elem.Shock: {
      e.stunT = Math.max(e.stunT, secs(SHOCK_STUN[L]));
      if (e.wetT > 0) electrocute(h, e, owner);
      // the chain: the nearest N within R, each for a share of the hit, armour-bypassing, stunned at the same level
      const n = L === 4 ? 999 : SHOCK_N[L] + (e.wetT > 0 ? h.handOf?.(owner)?.wetChain ?? 0 : 0); // Dowsing Rounds: one more off a wet robot
      const near = h.enemies.filter((o) => o !== e && !o.dead && len2(o.x - e.x, o.z - e.z) < SHOCK_R[L] + ENEMIES[o.type].radius).sort((a, b) => len2(a.x - e.x, a.z - e.z) - len2(b.x - e.x, b.z - e.z));
      for (const o of near.slice(0, n)) {
        if (!h.damageEnemy(o, Math.max(4, amount * SHOCK_RATIO[L]), AP.Medium, owner, 0, o.x, o.y + 1, o.z, true)) o.stunT = Math.max(o.stunT, secs(SHOCK_STUN[L]));
      }
      // a wet shooter standing in water takes a backfire from their own shock
      if (op && op.wetT > 0 && h.world.wetAt(op.x, op.z)) h.hurtPlayer(op, WET_BACKFIRE, op.x, op.z, 0, 0, 0, true);
      break;
    }
    case Elem.Acid: {
      e.stripN = Math.max(e.stripN, ACID_TIERS[L]);
      e.stripT = Math.max(e.stripT, secs(ACID_SECS[L]));
      e.vulnT = Math.max(e.vulnT, secs(5)); // the web's acid also softens (kept)
      break;
    }
    case Elem.Magnetic: {
      e.magCut = Math.max(e.magCut, MAG_CUT[L]); e.magT = secs(MAG_SECS);
      e.shieldRegenT = Math.max(e.shieldRegenT, secs(MAG_SECS));
      if (e.shield > 0) { const s = e.shield; e.shield = 0; h.emit(Ev.EnemyHit, [e.id, Math.round(s), 2, owner, e.x, e.y + 1, e.z]); } // the shield is wiped outright
      if (L === 4) {
        // Singularity: robots within reach are yanked toward the impact
        for (const o of h.enemies) {
          if (o === e || o.dead) continue;
          const dx = e.x - o.x, dz = e.z - o.z, d = len2(dx, dz);
          if (d > SINGULARITY_R + ENEMIES[o.type].radius || d < 0.01) continue;
          const f = SINGULARITY_FORCE / ENEMIES[o.type].weight;
          o.kx += (dx / d) * f; o.kz += (dz / d) * f;
          h.damageEnemy(o, Math.max(0.5, amount * 0.25), AP.Medium, owner, 0, o.x, o.y + 1, o.z, true);
          h.stagger(o, 9999);
        }
      }
      break;
    }
  }
  h.emit(Ev.Proc, [e.id, elem]);
  h.procd?.(e, elem, owner);
}
function burn(e: Enemy, dps: number, ticks: number, owner: number) {
  if (e.wetT > 0) return;
  e.burnDps = Math.max(e.burnDps, dps); e.burnT = Math.max(e.burnT, ticks); e.burnOwner = owner;
}
function freeze(e: Enemy, ticks: number) {
  if (ENEMIES[e.type].staggerRes >= 9999) { e.stunT = Math.max(e.stunT, Math.min(ticks, secs(1))); return; } // bosses only flinch
  e.frozenT = Math.max(e.frozenT, ticks); e.stunT = Math.max(e.stunT, ticks);
}
/** wet + shock: every wet robot within reach takes the jolt (armour-bypassing); wet cowboys in the same water too */
function electrocute(h: ElemHost, e: Enemy, owner: number) {
  const inWater = h.world.wetAt(e.x, e.z);
  const r = inWater ? 22 : WET_SHOCK_R;
  for (const o of h.enemies) {
    if (o.dead || o.wetT <= 0 || len2(o.x - e.x, o.z - e.z) > r) continue;
    if (!h.damageEnemy(o, WET_SHOCK_DMG, AP.Medium, owner, 8, o.x, o.y + 1, o.z, true)) o.stunT = Math.max(o.stunT, secs(0.45));
    o.wetT = 0;
  }
  if (inWater) for (const p of h.players.values()) {
    if (p.phase !== 0 || p.wetT <= 0 || !h.world.wetAt(p.x, p.z) || len2(p.x - e.x, p.z - e.z) > r) continue;
    if (p.id === owner) h.hurtPlayer(p, 12, e.x, e.z, 0, 0, 0, true); else h.friendlyHurt(p, owner, 12, e.x, e.z, 0, 0, true);
    p.hitStunT = Math.max(p.hitStunT, secs(0.3));
  }
  h.emit(Ev.Explosion, [e.x, e.y + 0.5, e.z, r * 0.5, 5]);
}
/** the frozen window and the ice slow, read by the damage and steering paths */
export const frozenMul = (e: Enemy) => (e.frozenT > 0 ? FROZEN_DMG_MUL : 1);
export const iceSlowMul = (e: Enemy) => Math.max(0.1, 1 - e.iceSlow);
/** RPS: a hit of `elem` on a robot carrying armorEl it beats: x1.5 and a 2 s one-tier strip (resolve_rps_bonus) */
export function weaknessMul(e: Enemy, elem: number): number {
  if (elem === Elem.None || e.armorEl === Elem.None || !beats(elem, e.armorEl)) return 1;
  e.stripN = Math.max(e.stripN, 1); e.stripT = Math.max(e.stripT, WEAKNESS_STRIP);
  return WEAKNESS_MUL;
}

// ------------------------------------------------------------------ elements on cowboys (player.gd / health.gd)
/** an elemental hit landed on a cowboy (an elemental robot's bolt or bite, a hazard): the status, at the player curve */
export function procPlayer(h: ElemHost, p: PlayerState, elem: number, level: number, force = false) {
  if (p.phase !== 0) return;
  if (!force && h.rng.next() >= PROC_CHANCE[Math.max(1, Math.min(4, level))]) return;
  switch (elem) {
    case Elem.Fire: p.burnT = Math.max(p.burnT, secs(3)); break; // 8 HP/s through the shield (player.ts)
    case Elem.Ice: p.iceN = Math.min(3, p.iceN + 1); p.iceT = secs(3); break; // 10 % a stack, cap 3 (defs.ts ICE_*)
    case Elem.Shock: p.hitStunT = Math.max(p.hitStunT, secs(0.3)); break;
    case Elem.Acid: p.acidT = Math.max(p.acidT, secs([0, 5, 10, 10, 999][Math.min(4, level)])); break; // +15 % hull damage
    case Elem.Magnetic: p.shieldDelay = Math.max(p.shieldDelay, secs(MAG_SECS)); break;
  }
}
export const ACID_VULN_MUL = 1.15;
export const PLAYER_BURN_DPS = 8;

// ------------------------------------------------------------------ hazards and reactions (scripts/hazards/*.gd)
/** on the wire these are Ev.Hazard element ids matching the renderer's El: Fire 0, Acid 1, Shock 2, Ice 3, Steam 5, Tornado 6 */
export const enum HazKind { Fire = 0, Acid = 1, Shock = 2, Ice = 3, Steam = 5, Tornado = 6 }
export interface Hazard { id: number; kind: HazKind; owner: number; x: number; y: number; z: number; r: number; t: number; vx: number; vz: number }
const HAZ: Record<number, { r: number; secs: number }> = { [HazKind.Fire]: { r: 3, secs: 5 }, [HazKind.Acid]: { r: 3.5, secs: 6 }, [HazKind.Shock]: { r: 4, secs: 6 }, [HazKind.Ice]: { r: 4, secs: 6 }, [HazKind.Steam]: { r: 5, secs: 4 }, [HazKind.Tornado]: { r: 4, secs: 3 } };
const HAZ_PULSE = 15; // ticks between bites (0.25 s)
let nextHazId = 1;
export function hazardKind(elem: number): HazKind | -1 { return elem === Elem.Fire ? HazKind.Fire : elem === Elem.Acid ? HazKind.Acid : elem === Elem.Shock ? HazKind.Shock : elem === Elem.Ice ? HazKind.Ice : -1; }
/** an elemental grenade (or an L3+ gun's big hit) leaves its hazard on the ground */
export function placeHazard(h: ElemHost, list: Hazard[], kind: HazKind, owner: number, x: number, y: number, z: number, scale = 1): Hazard {
  const d = HAZ[kind];
  const hz: Hazard = { id: nextHazId++ & 0xffff || 1, kind, owner, x, y, z, r: d.r * scale, t: secs(d.secs * scale), vx: 0, vz: 0 };
  if (kind === HazKind.Tornado) { const a = h.rng.range(0, Math.PI * 2); hz.vx = Math.cos(a) * 1.5; hz.vz = Math.sin(a) * 1.5; }
  list.push(hz);
  h.emit(Ev.Hazard, [hz.id, kind, x, y, z, hz.r, hz.t]);
  return hz;
}
/** an elemental hit landed at (x,z): a hazard within reach that this element beats reacts (Godot try_combo) */
export function tryCombo(h: ElemHost, list: Hazard[], elem: number, owner: number, x: number, y: number, z: number): boolean {
  for (let i = list.length - 1; i >= 0; i--) {
    const hz = list[i];
    if (len2(x - hz.x, z - hz.z) > hz.r + 1 || Math.abs(y - hz.y) > 3) continue;
    const hzEl = hz.kind === HazKind.Fire ? Elem.Fire : hz.kind === HazKind.Acid ? Elem.Acid : hz.kind === HazKind.Shock ? Elem.Shock : hz.kind === HazKind.Ice ? Elem.Ice : Elem.None;
    if (hzEl === Elem.None || !beats(elem, hzEl)) continue;
    list.splice(i, 1);
    const rm = h.handOf?.(owner)?.reactionMul ?? 1; // Catalyst, the conduit set
    h.emit(Ev.Hazard, [hz.id, hz.kind, hz.x, hz.y, hz.z, hz.r, 0]); // consumed
    if (hz.kind === HazKind.Acid) placeHazard(h, list, HazKind.Tornado, owner, hz.x, hz.y, hz.z, rm); // MELTDOWN: a fire tornado (Catalyst: bigger, longer)
    else if (hz.kind === HazKind.Fire) placeHazard(h, list, HazKind.Steam, owner, hz.x, hz.y, hz.z); // THERMAL SHOCK: a steam cloud
    else if (hz.kind === HazKind.Shock) {
      // SHATTER (MagnetoArc): 30 to the nearest three within 12 m
      const near = h.enemies.filter((o) => !o.dead && len2(o.x - hz.x, o.z - hz.z) < 12).sort((a, b) => len2(a.x - hz.x, a.z - hz.z) - len2(b.x - hz.x, b.z - hz.z)).slice(0, 3);
      for (const o of near) h.damageEnemy(o, 30 * rm, AP.Medium, owner, 8, o.x, o.y + 1, o.z, true);
      h.emit(Ev.Explosion, [hz.x, hz.y + 0.5, hz.z, 6, 5]);
    } else if (hz.kind === HazKind.Ice) {
      // SUPERCONDUCT (ChainShock): 25 within 5 m, a 0.5 s stun and two more ice stacks
      for (const o of h.enemies) {
        if (o.dead || len2(o.x - hz.x, o.z - hz.z) > 5 + ENEMIES[o.type].radius) continue;
        if (!h.damageEnemy(o, 25 * rm, AP.Medium, owner, 8, o.x, o.y + 1, o.z, true)) { o.stunT = Math.max(o.stunT, secs(0.5)); o.iceN = Math.min(5, o.iceN + 2); o.iceSlow = Math.max(o.iceSlow, o.iceN * 0.2); }
      }
      h.emit(Ev.Explosion, [hz.x, hz.y + 0.5, hz.z, 5, 6]);
    }
    h.emit(Ev.Notice, [], hz.kind === HazKind.Acid ? 'MELTDOWN' : hz.kind === HazKind.Fire ? 'THERMAL SHOCK' : hz.kind === HazKind.Shock ? 'SHATTER' : 'SUPERCONDUCT');
    return true;
  }
  return false;
}
/** the hazards bite every HAZ_PULSE ticks; the tornado drifts */
export function stepHazards(h: ElemHost, list: Hazard[]) {
  for (let i = list.length - 1; i >= 0; i--) {
    const hz = list[i];
    if (--hz.t <= 0) { list.splice(i, 1); continue; }
    if (hz.kind === HazKind.Tornado) { hz.x += hz.vx * DT; hz.z += hz.vz * DT; }
    if (hz.t % HAZ_PULSE !== 0) continue;
    const dt = HAZ_PULSE * DT;
    const inside = (x: number, y: number, z: number) => len2(x - hz.x, z - hz.z) < hz.r && y > hz.y - 3 && y < hz.y + 6;
    for (const e of h.enemies) {
      if (e.dead || !inside(e.x, e.y, e.z)) continue;
      switch (hz.kind) {
        case HazKind.Fire: if (!h.damageEnemy(e, 6 * dt, AP.Light, hz.owner, 0, e.x, e.y + 1, e.z, true)) burn(e, 6, secs(2), hz.owner); break;
        case HazKind.Acid: if (!h.damageEnemy(e, 6 * dt, AP.Light, hz.owner, 0, e.x, e.y + 1, e.z, true)) { e.stripN = Math.max(e.stripN, 1); e.stripT = Math.max(e.stripT, secs(1)); } break;
        case HazKind.Shock: if (hz.t % 60 === 0 && !h.damageEnemy(e, 4, AP.Light, hz.owner, 0, e.x, e.y + 1, e.z, true)) e.stunT = Math.max(e.stunT, secs(0.3)); break;
        case HazKind.Ice: if (hz.t % 60 === 0 && e.frozenT <= 0) { e.iceN = Math.min(5, e.iceN + 1); e.iceSlow = Math.max(e.iceSlow, e.iceN * 0.2); } break;
        case HazKind.Steam: e.slowT = Math.max(e.slowT, secs(0.5)); break;
        case HazKind.Tornado: if (!h.damageEnemy(e, 12 * dt, AP.Light, hz.owner, 0, e.x, e.y + 1, e.z, true)) burn(e, 8, secs(2), hz.owner); break;
      }
    }
    // the posse feels them too, never the one who laid it, at 0.6x (CLAUDE.md gotcha 11)
    for (const p of h.players.values()) {
      if (p.phase !== 0 || p.id === hz.owner || !inside(p.x, p.y, p.z)) continue;
      switch (hz.kind) {
        case HazKind.Fire: h.friendlyHurt(p, hz.owner, 6 * dt * 0.6, hz.x, hz.z, 0, 0, true); p.burnT = Math.max(p.burnT, secs(1)); break;
        case HazKind.Acid: h.friendlyHurt(p, hz.owner, 6 * dt * 0.6, hz.x, hz.z, 0, 0, true); p.acidT = Math.max(p.acidT, secs(1)); break;
        case HazKind.Shock: if (hz.t % 60 === 0) { h.friendlyHurt(p, hz.owner, 4 * 0.6, hz.x, hz.z, 0, 0, true); p.hitStunT = Math.max(p.hitStunT, secs(0.3)); } break;
        case HazKind.Ice: if (hz.t % 60 === 0) { p.iceN = Math.min(3, p.iceN + 1); p.iceT = secs(3); } break;
        case HazKind.Tornado: h.friendlyHurt(p, hz.owner, 12 * dt * 0.6, hz.x, hz.z, 0, 0, true); p.burnT = Math.max(p.burnT, secs(1)); break;
        default: break;
      }
    }
  }
}
